import React, { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import axios from '../../../api/axios';

type CategoryOption = {
    _id: string;
    name: string;
};

type AddProductModalProps = {
    show: boolean;
    setShow: (value: boolean) => void;
    token: string | null;
    categories: CategoryOption[];
    onSaved: () => Promise<void> | void;
};

const AddProductModal = ({ show, setShow, token, categories, onSaved }: AddProductModalProps) => {
    const [error, setError] = useState('');
    const [productData, setProductData] = useState({
        name: '',
        companyName: '',
        description: '',
        price: '',
        category: '',
        subcategory: '',
        sizes: '',
        stock: '',
        sku: '',
        tags: '',
        discount: '',
        isActive: true,
        isFeatured: false,
    });
    const [imageSlots, setImageSlots] = useState<Array<File | null>>([null, null, null, null, null]);

    const resetForm = () => {
        setProductData({
            name: '',
            companyName: '',
            description: '',
            price: '',
            category: '',
            subcategory: '',
            sizes: '',
            stock: '',
            sku: '',
            tags: '',
            discount: '',
            isActive: true,
            isFeatured: false,
        });
        setImageSlots([null, null, null, null, null]);
    };

    const setImageAt = (index: number, file: File | null) => {
        setImageSlots((prev) => prev.map((item, i) => (i === index ? file : item)));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
        setProductData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('Authentication is required. Please login again.');
            return;
        }

        const selectedImages = imageSlots.filter((file): file is File => Boolean(file));

        if (selectedImages.length < 1 || selectedImages.length > 5) {
            setError('Please upload at least 1 and at most 5 images.');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('name', productData.name.trim());
            if (productData.companyName.trim()) {
                formData.append('companyName', productData.companyName.trim());
            }
            formData.append('description', productData.description.trim());
            formData.append('price', String(Number(productData.price)));
            formData.append('category', productData.category);
            formData.append('subcategory', productData.subcategory.trim());
            formData.append('sizes', productData.sizes);
            formData.append('stock', String(Number(productData.stock)));
            if (productData.sku.trim()) {
                formData.append('sku', productData.sku.trim());
            }
            if (productData.tags.trim()) {
                formData.append('tags', productData.tags);
            }
            if (productData.discount) {
                formData.append('discount', String(Number(productData.discount)));
            }
            formData.append('isActive', String(productData.isActive));
            formData.append('isFeatured', String(productData.isFeatured));
            selectedImages.forEach((file) => formData.append('images', file));

            await axios.post(
                '/products',
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            await onSaved();
            setShow(false);
            resetForm();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to add product.');
        }
    };

    return (
        <Modal show={show} onHide={() => setShow(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Add Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="addProductName">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" name="name" value={productData.name} onChange={handleChange} required />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductDescription">
                        <Form.Label>Description</Form.Label>
                        <Form.Control as="textarea" name="description" value={productData.description} onChange={handleChange} required />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductCompanyName">
                        <Form.Label>Company Name (optional)</Form.Label>
                        <Form.Control type="text" name="companyName" value={productData.companyName} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductPrice">
                        <Form.Label>Price</Form.Label>
                        <Form.Control type="number" min="0" step="0.01" name="price" value={productData.price} onChange={handleChange} required />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductCategory">
                        <Form.Label>Category</Form.Label>
                        <Form.Select name="category" value={productData.category} onChange={handleChange} required>
                            <option value="">Select category</option>
                            {categories.map((category) => (
                                <option key={category._id} value={category._id}>{category.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductSubcategory">
                        <Form.Label>Subcategory (optional)</Form.Label>
                        <Form.Control type="text" name="subcategory" value={productData.subcategory} onChange={handleChange} placeholder="e.g. Analog Watches" />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductSizes">
                        <Form.Label>Sizes (optional, comma separated)</Form.Label>
                        <Form.Control type="text" name="sizes" value={productData.sizes} onChange={handleChange} placeholder="e.g. Small, Medium, Large" />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductStock">
                        <Form.Label>Stock</Form.Label>
                        <Form.Control type="number" min="0" name="stock" value={productData.stock} onChange={handleChange} required />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductSku">
                        <Form.Label>SKU (optional)</Form.Label>
                        <Form.Control type="text" name="sku" value={productData.sku} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductTags">
                        <Form.Label>Tags (comma separated)</Form.Label>
                        <Form.Control type="text" name="tags" value={productData.tags} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductImagesPrimary">
                        <Form.Label>Primary Image (required)</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(0, input.files?.[0] || null);
                            }}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductImages2">
                        <Form.Label>Optional Image 2</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(1, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductImages3">
                        <Form.Label>Optional Image 3</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(2, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductImages4">
                        <Form.Label>Optional Image 4</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(3, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductImages5">
                        <Form.Label>Optional Image 5</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(4, input.files?.[0] || null);
                            }}
                        />
                        <Form.Text muted>{imageSlots.filter(Boolean).length} file(s) selected</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="addProductDiscount">
                        <Form.Label>Discount % (optional)</Form.Label>
                        <Form.Control type="number" min="0" max="100" name="discount" value={productData.discount} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Active Product"
                            name="isActive"
                            checked={productData.isActive}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Featured Product"
                            name="isFeatured"
                            checked={productData.isFeatured}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Button variant="dark" type="submit">Add Product</Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AddProductModal;

